import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const svgDrawOnSkill: Skill = {
    name: SKILL_NAMES.svgDrawOn,
    kind: 'codeo',
    description:
        'Draw a stroke on screen with stroke-dash animation — hand-drawn underlines beneath a ' +
        'key word, traced rings or arcs, growing chart lines, connector paths between elements.',
    instructions: `# SVG Draw-On

A stroke draws itself across the screen: set the path's \`stroke-dasharray\` to its own length, offset it fully, then tween the offset to 0. Reads as hand-drawn intent — an underline appearing under the word as it's spoken, a ring tracing around a number, a chart line growing.

## Markup

\`\`\`html
<svg id="underline" width="420" height="24" viewBox="0 0 420 24"
     style="position: absolute; left: 750px; top: 620px;">
  <path id="underline-path" d="M4 18 C 120 6, 300 6, 416 14"
        fill="none" stroke="var(--l5-color-1, #7a6dff)" stroke-width="6" stroke-linecap="round"/>
</svg>
\`\`\`

A gentle S-curve (\`C\` command with control points a few px off the baseline) reads as hand-drawn; a dead-straight \`L\` line reads as a border. The path is pure geometry.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const underlinePath = document.querySelector('#underline-path');
const underlineLen = underlinePath.getTotalLength();
gsap.set(underlinePath, { strokeDasharray: underlineLen, strokeDashoffset: underlineLen });
tl.to(underlinePath, { strokeDashoffset: 0, duration: 0.7, ease: 'power2.inOut' }, 2.2);
window.__timelines = { main: tl };
\`\`\`

The build-time \`gsap.set\` hides the stroke before the timeline runs — equivalent to the sanctioned \`gsap.set(el, { opacity: 0 })\` pattern for late entrances.

## Adapt

- **Underline a spoken word**: position the draw at the word's onset, duration 0.5–0.8, \`power2.inOut\`.
- **Ring/circle around a value**: a circular path (or \`<circle>\` — \`getTotalLength\` works on it) drawn over ~0.9s lands well started just before the value's onset.
- **Chart line**: duration 1.2–2.0 with \`power1.inOut\`; pair each vertex's arrival with that data point's spoken onset if narrated.
- **Reverse direction**: don't tween \`strokeDashoffset\` to a negative length — flip the path's direction in the \`d\` attribute instead; negative offsets double-draw on some path shapes.
- **Draw-off exit**: tween offset from 0 back to the length.

## Pitfalls

- Give the \`<svg>\` explicit \`width\`/\`height\` attributes — a viewBox-only svg with no explicit size won't size predictably in the DOM and can stretch to fill its container.
- \`getTotalLength()\` needs the real path in the DOM; the timeline script runs after the markup, so this just works — don't move it into a callback.
- Round caps (\`stroke-linecap: round\`) sell the hand-drawn read; butt caps look mechanical.
- The draw reveals geometry, not text — the word being underlined still needs its own entrance tween; drawing the underline doesn't substitute for revealing the word.`,
};
